import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { HC } from "./theme";
import { Card, SectionHead, Btn, Empty } from "./ui";
import { HistoryPanel } from "../../components/QueriesBoard";
import { toast } from "sonner";
import { AlertTriangle, ShieldCheck, Clock, Gavel, Send, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";

const SEV = {
  "Note": ["#F3ECE2", HC.soft],
  "Warning": ["#FDF3DD", HC.amber],
  "Final warning": ["#FBE9E9", HC.red],
};

const Chip = ({ tint, children }) => (
  <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full whitespace-nowrap"
    style={{ background: tint[0], color: tint[1] }}>{children}</span>
);

export default function Escalations() {
  const [data, setData] = useState({ rows: [], counts: {}, outcomes: {}, read_only: false });
  const [includeClosed, setIncludeClosed] = useState(false);
  const [open, setOpen] = useState(null);
  const [history, setHistory] = useState(null);
  const [reply, setReply] = useState("");
  const [decision, setDecision] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const d = await api.get("/hr/queries/escalations", { params: { include_closed: includeClosed } });
      setData(d);
      if (open) setOpen(d.rows.find((r) => r.id === open.id) || null);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeClosed]);

  const openThread = async (r) => {
    setOpen(r); setReply(""); setHistory(null);
    try { setHistory(await api.get("/hr/queries/history", { params: { employee_id: r.employee_id } })); }
    catch { /* history is optional context */ }
  };

  const act = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); toast.success(msg); setOpen(r); setDecision(null); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const STATS = [
    ["Needs attention", data.counts.needs_attention || 0, HC.red, AlertTriangle],
    ["Unacknowledged", data.counts.unacknowledged || 0, HC.amber, Clock],
    ["Open escalations", data.counts.escalated || 0, HC.burgundy, ShieldCheck],
    ["Closed by HR", data.counts.closed || 0, HC.text, Gavel],
  ];

  return (
    <div className="space-y-4" data-testid="hr-escalations-page">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {STATS.map(([l, v, c, Icon]) => (
          <div key={l} className="rounded-2xl border p-4 flex items-center justify-between"
            style={{ borderColor: HC.border, background: HC.panel }} data-testid={`esc-stat-${l.toLowerCase().replace(/\s+/g, "-")}`}>
            <div>
              <div className="text-[10px] font-bold tracking-[0.14em]" style={{ color: HC.soft }}>{l.toUpperCase()}</div>
              <div className="font-brand text-3xl font-bold" style={{ color: c }}>{v}</div>
            </div>
            <Icon size={24} strokeWidth={1.4} style={{ color: HC.gold }} />
          </div>
        ))}
      </div>

      <Card>
        <SectionHead
          title="Escalation queue"
          sub={data.read_only
            ? "Escalations you raised — HR owns the decision from here."
            : "Oldest first, with anything unacknowledged or untouched for 3+ days flagged. Take ownership, reply, then record the decision."}
          right={<Btn variant="outline" className="h-9" onClick={() => setIncludeClosed(!includeClosed)} data-testid="esc-toggle-closed">
            {includeClosed ? "Hide closed" : "Show closed"}
          </Btn>} />

        {data.rows.length === 0 ? <Empty text="Nothing escalated — the queue is clear." /> : (
          <div className="space-y-2" data-testid="esc-list">
            {data.rows.map((r) => (
              <button key={r.id} onClick={() => openThread(r)} data-testid={`esc-row-${r.id}`}
                className="w-full text-left rounded-xl border p-3 hover:shadow-sm transition-shadow"
                style={{ borderColor: r.needs_attention ? "#E8C9C9" : HC.border,
                          background: r.needs_attention ? "#FDF3F3" : HC.panelSoft }}>
                <div className="flex items-center gap-2 flex-wrap">
                  {r.needs_attention && <Chip tint={["#FBE9E9", HC.red]}>Needs attention</Chip>}
                  <Chip tint={SEV[r.severity] || SEV.Note}>{r.severity}</Chip>
                  <Chip tint={["#F3ECE2", HC.soft]}>{r.category}</Chip>
                  {r.status === "closed" && <Chip tint={["#E8F3E8", HC.green]}>{r.hr_outcome_label}</Chip>}
                  <span className="ml-auto text-[11px]" style={{ color: HC.soft }}>
                    {r.days_waiting}d waiting{r.acknowledged ? ` · with ${r.hr_acknowledged_by}` : ""}
                  </span>
                </div>
                <div className="font-semibold text-sm mt-1" style={{ color: HC.text }}>{r.subject}</div>
                <div className="text-[11.5px]" style={{ color: HC.muted }}>
                  {r.to_name} ({r.employee_no || "—"}) · {r.location_name} · escalated by {r.from_name}
                  {r.outcome_note ? ` — “${r.outcome_note}”` : ""}
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-xl" data-testid="esc-thread-dialog">
          {open && (<>
            <DialogHeader>
              <DialogTitle className="font-brand pr-8">{open.subject}</DialogTitle>
              <DialogDescription className="flex flex-wrap items-center gap-2 pt-1">
                <Chip tint={SEV[open.severity] || SEV.Note}>{open.severity}</Chip>
                <Chip tint={["#F3ECE2", HC.soft]}>{open.category}</Chip>
                <span style={{ color: HC.muted }}>{open.to_name} · {open.location_name} · escalated by {open.from_name}</span>
              </DialogDescription>
            </DialogHeader>

            {history && <HistoryPanel h={history} compact />}

            <div className="max-h-56 overflow-y-auto space-y-2 pr-1" data-testid="esc-thread-messages">
              {(open.messages || []).map((m) => (
                <div key={m.id} className="rounded-xl border px-3 py-2"
                  style={{ borderColor: HC.border, background: m.system ? "#F3ECE2" : HC.panelSoft }}>
                  <div className="text-[11px] font-semibold" style={{ color: HC.burgundy }}>{m.author_name}
                    <span className="ml-2 font-normal" style={{ color: HC.soft }}>{(m.at || "").slice(0, 16).replace("T", " ")}</span></div>
                  <div className="text-sm whitespace-pre-wrap" style={{ color: HC.text }}>{m.body}</div>
                </div>
              ))}
            </div>

            {open.status === "closed" ? (
              <div className="rounded-xl border px-3 py-2 text-sm" style={{ borderColor: HC.border, background: "#E8F3E8", color: HC.text }} data-testid="esc-closed-note">
                <b>{open.hr_outcome_label}</b> — {open.hr_outcome_note} ({open.hr_closed_by})
              </div>
            ) : data.read_only ? (
              <div className="rounded-xl border px-3 py-2 text-sm" style={{ borderColor: HC.border, background: "#F3ECE2", color: HC.soft }} data-testid="esc-readonly-note">
                {open.acknowledged ? `HR (${open.hr_acknowledged_by}) has taken ownership.` : "Waiting for HR to take ownership."}
              </div>
            ) : (<>
              <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Add a note to the thread…" data-testid="esc-reply" />
              <DialogFooter className="flex-wrap gap-2">
                {!open.acknowledged && (
                  <Btn variant="outline" disabled={busy} data-testid="esc-acknowledge"
                    onClick={() => act(() => api.post(`/hr/queries/${open.id}/acknowledge`), "You now own this escalation")}>
                    <ShieldCheck size={14} /> Take ownership
                  </Btn>
                )}
                <Btn variant="outline" disabled={busy || !reply.trim()} data-testid="esc-reply-send"
                  onClick={() => act(async () => { const r = await api.post(`/hr/queries/${open.id}/reply`, { body: reply, status: "escalated" }); setReply(""); return r; }, "Note added")}>
                  <Send size={14} /> Add note
                </Btn>
                <Btn disabled={busy} data-testid="esc-decide"
                  onClick={() => setDecision({ outcome: "resolved", note: "" })}>
                  <Gavel size={14} /> Record decision
                </Btn>
              </DialogFooter>
            </>)}
          </>)}
        </DialogContent>
      </Dialog>

      <Dialog open={!!decision} onOpenChange={(o) => !o && setDecision(null)}>
        <DialogContent className="max-w-md" data-testid="esc-decision-dialog">
          <DialogHeader>
            <DialogTitle className="font-brand">HR decision</DialogTitle>
            <DialogDescription>This closes the query. Both the manager and the staff member are notified.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {Object.entries(data.outcomes || {}).map(([k, label]) => (
              <label key={k} className="flex items-center gap-2.5 rounded-lg border px-3 py-2 cursor-pointer"
                style={{ borderColor: decision?.outcome === k ? HC.burgundy : HC.border, background: HC.panelSoft }}
                data-testid={`esc-outcome-${k}`}>
                <input type="radio" checked={decision?.outcome === k} onChange={() => setDecision({ ...decision, outcome: k })} />
                <span className="text-sm" style={{ color: HC.text }}>{label}</span>
              </label>
            ))}
            <Textarea rows={3} value={decision?.note || ""} data-testid="esc-decision-note"
              onChange={(e) => setDecision({ ...decision, note: e.target.value })}
              placeholder="What was decided and why?" />
          </div>
          <DialogFooter>
            <Btn variant="outline" onClick={() => setDecision(null)}><X size={14} /> Cancel</Btn>
            <Btn disabled={busy || !decision?.note?.trim()} data-testid="esc-decision-submit"
              onClick={() => act(() => api.post(`/hr/queries/${open.id}/hr-close`, decision), "Decision recorded")}>
              Confirm decision
            </Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
